import { useMemo } from "react"

import { IconFilter } from "@tabler/icons-react"
import { ColumnFiltersState } from "@tanstack/react-table"
import { match } from "ts-pattern"

import { Button } from "../../Button"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger,
} from "../../DropdownMenu"
import { KEBAB_MENU_COLUMN_ID, SELECTABLE_COLUMN_ID } from "../DataTable"
import { LE_TableInstance } from "../types"
import { BooleanFilter } from "./BooleanFilter"
import { DateRangeFilter } from "./DateRangeFilter"
import { MultiEnumFilter } from "./MultiEnumFilter"
import { NumberRangeFilter } from "./NumberRangeFilter"
import { SingleEnumFilter } from "./SingleEnumFilter"

const FILTER_MENU_BUTTON_ID_SUFFIX = "filter-menu-button"
const FILTER_MENU_POPOVER_ID_SUFFIX = "filter-menu-popover"

export function FilterButton<T extends Record<string, any> = object>({
  table,
  columnFilters,
}: {
  table: LE_TableInstance<T>
  columnFilters: ColumnFiltersState
}) {
  const { getAllFlatColumns } = table
  const columns = getAllFlatColumns()

  const allColumns = useMemo(() => {
    return (
      columns
        .filter(
          (col) =>
            col.columns.length === 0 &&
            (col.getCanFilter() || col.columnDef.enableColumnFilter),
        )
        // sort filter list alphabetically
        .sort((a, b) =>
          (a.columnDef.header ?? "") > (b.columnDef.header ?? "") ? 1 : -1,
        )
    )
  }, [columns])

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          id={table.getElementId(FILTER_MENU_BUTTON_ID_SUFFIX)}
          variant="neutral"
          icon={IconFilter}
          className="h-fit"
        >
          Filter
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align="start"
        id={table.getElementId(FILTER_MENU_POPOVER_ID_SUFFIX)}
      >
        <DropdownMenuLabel>All filters</DropdownMenuLabel>
        <DropdownMenuSeparator />
        <div className="max-h-96 overflow-auto">
          {allColumns.map((col) => {
            const label = col.columnDef.meta?.textLabel ?? col.columnDef.header
            if (
              !label ||
              !col.columnDef.filterFn ||
              // filter built-in columns like selectable and kebab menu
              (col.columnDef.id &&
                [SELECTABLE_COLUMN_ID, KEBAB_MENU_COLUMN_ID].includes(col.columnDef.id))
            ) {
              return null
            }

            if (typeof label !== "string") {
              throw new Error(
                "column `header` must be a string, or `meta.textLabel` must be set.",
              )
            }

            const filterValue = columnFilters?.find((f) => f.id === col.id)?.value

            return (
              <DropdownMenuSub key={label}>
                <DropdownMenuSubTrigger>{label}</DropdownMenuSubTrigger>
                <DropdownMenuSubContent hideWhenDetached>
                  {match(col.columnDef.filterFn)
                    .with("multiEnum", () => (
                      <MultiEnumFilter
                        col={col}
                        filterValue={filterValue as string[]}
                        table={table}
                      />
                    ))
                    .with("singleEnum", () => (
                      <SingleEnumFilter
                        col={col}
                        filterValue={filterValue as string}
                        table={table}
                      />
                    ))
                    .with("inNumberRange", () => (
                      <NumberRangeFilter
                        col={col}
                        filterValue={filterValue as [number, number, boolean]}
                      />
                    ))
                    .with("inDateRange", () => (
                      <DateRangeFilter
                        col={col}
                        filterValue={filterValue as [string, string, boolean]}
                      />
                    ))
                    .with("boolean", () => (
                      <BooleanFilter col={col} filterValue={filterValue as boolean} />
                    ))
                    .otherwise(() => null)}
                </DropdownMenuSubContent>
              </DropdownMenuSub>
            )
          })}
        </div>
      </DropdownMenuContent>
    </DropdownMenu>
  )
}
